import debug from 'debug';
import numeral from 'numeral';
import { sprintf } from 'sprintf-js';
import * as _ from 'lodash-es';
import { uniqueID } from './unique-id';
import { parseISO, format, add, Duration } from 'date-fns';
import * as dateFns from 'date-fns';
import { AxiosResponse } from 'axios';
import { Application } from './application';
import humanizeDuration from 'humanize-duration';
import {
  Openapi,
  PagingDataSourceImpl,
  createSearchDataSource,
  CRUD,
  parseConnectionString,
  SlickDataSourceOptions,
  createSlickDataSource,
} from './connections';
import { asFormData } from './connections';
import { toYamlString, imageData } from './helpers';
import { Resource } from './resource';
import { Poller, PollerOptions } from './poller';
import { unaccent } from '@integro/gettext';
import { ResourceProxy, ResourceProxyOptions } from './resource-proxy';
import { CollectionBasedPagingDataSource, Pager } from './pagination';
import * as downloads from './downloads';

const log = debug('itp:core:fn');

export interface FetchOptions {}

export type GlobalHelpers = {
  dateFns: any;
  _: any;

  sprintf: Function;
  asFormData: Function;
  toYamlString: Function;
  parseISODate: Function;
  formatDate: Function;
  today: Function;
  dateAdd: Function;
  epoch: Function;
  isWeekend: Function;
  gettext: Function;
  ngettext: Function;
  pgettext: Function;
  npgettext: Function;
  crud: Function;
  fetch: Function;
  schemaDefaults: Function;
  getSchema: Function;
  getEnumValuesFromSchema: Function;
  pagingDataSource: Function;
  collectionBasedPagingDatasource: Function;
  pager: Function;
  searchDataSource: Function;
  slickDataSource: Function;
  randomElementId: Function;
  newResourceID: Function;
  Resource: Function;
  urlFor: Function;
  poller: Function;
  resourceProxy: Function;
  imageData: Function;
  numeral: Function;
  humanizeDuration: Function;
  showFileInNewTab: Function;
  withErrorHandler: Function;
};

export function fns(app: Application): GlobalHelpers {
  return {
    dateFns,
    _: _,

    sprintf,

    asFormData,

    toYamlString,

    parseISODate: parseISO,
    formatDate: format,

    today() {
      return format(new Date(), 'yyyy-MM-dd');
    },

    dateAdd(d: any, duration: Duration) {
      return format(add(parseISO(d), duration), 'yyyy-MM-dd');
    },

    epoch(argument: string) {
      return parseISO(argument).valueOf();
    },

    isWeekend(d: Date) {
      if (_.isString(d)) {
        d = parseISO(d);
      }

      if (_.isDate(d)) {
        const dayOfWeek = d.getDay();
        return dayOfWeek === 6 || dayOfWeek === 0; // 6 = Saturday, 0 = Sunday
      }
      return false;
    },

    gettext(msgid: string, conversion?: string | string[], args?: Record<string, any>) {
      return app.i18n.translate(msgid, { conversion, args });
    },

    ngettext(msgid: string, plural: string, n: number, conversion?: string | string[], args?: Record<string, any>) {
      return app.i18n.translate(msgid, { plural, n, conversion, args });
    },

    pgettext(context: string, msgid: string, conversion?: string | string[], args?: Record<string, any>) {
      return app.i18n.translate(msgid, { context, conversion, args });
    },

    npgettext(context: string, msgid: string, plural: string, n: number, conversion?: string | string[], args?: Record<string, any>) {
      return app.i18n.translate(msgid, { context, plural, n, conversion, args });
    },

    crud(objectType: string) {
      return CRUD.createCRUD(objectType, app);
    },

    fetch(operation: string | { url: string }, options: Dictionary<any>) {
      if (_.isString(operation)) {
        options = options || { asResource: false, asResourceContainer: false };

        const isBase64ImageDataRequest =
          options.responseConverter === 'base64imagedata';
        if (isBase64ImageDataRequest) {
          options.responseType = 'arraybuffer';
        }

        const cn = parseConnectionString(operation);
        const conn = app.connections.getConnection(cn.connection) as Openapi;

        return conn
          .request(operation, options)
          .then((response) => onResponse(response));

        function onResponse(response: AxiosResponse) {
          if (response.status === 204) {
            return null;
          }

          if (response.status === 200 && isBase64ImageDataRequest) {
            return imageData(response);
          }

          if (options.asResource && _.isArray(response.data)) {
            const res = _.map(response.data, (p) => new Resource(p));
            log('fetch-resource %o', res);
            return res;
          }

          if (options.asResource && _.isObject(response.data)) {
            const res = new Resource(response.data);
            log('fetch-resource %o', res);
            return res;
          }

          if (options.asResourceContainer && _.isArray(response.data)) {
            const res = {
              object: 'list',
              self: response.config.url,
              data: _.map(response.data, (p) => new Resource(p)),
            };
            log('fetch-resource-container %o', res);
            return res;
          }

          return response.data;
        }
      }

      const conn = app.connections.default as Openapi;
      const url = `${conn.config.baseUrl}${operation.url}`;

      return app.connections.ajax(url);
    },

    schemaDefaults(name: string, extras?: object, asResource: boolean = true) {
      const d = _.extend(
        {
          id: null,
          links: { self: null },
        },
        (app.connections.default as Openapi).getSchemaDefaults(name),
        extras,
      );
      return asResource ? new Resource(d) : d;
    },

    getSchema(name: string) {
      return (app.connections.default as Openapi).getSchema(name);
    },

    getEnumValuesFromSchema(typeName: string, options?: { sort: boolean }) {
      const schema = (app.connections.default as Openapi).getSchema(typeName);
      const sort = options && options.sort != null ? options.sort : true;

      if (schema && schema.enum) {
        const result = _.chain(schema.enum).map((p) => ({
          value: p,
          text: app.i18n.translate(p, {
            context: typeName,
            conversion: 'Sentence',
          }),
        }));

        if (sort) {
          return result.sortBy((p) => unaccent(p.text).toLowerCase()).value();
        }
        return result.value();
      }
    },

    pagingDataSource(operation: string) {
      return new PagingDataSourceImpl<object>(
        app.connections.default as Openapi,
        operation,
      );
    },

    collectionBasedPagingDatasource(collection: Array<any>, pageSize: number) {
      return new CollectionBasedPagingDataSource(collection, pageSize);
    },

    pager(collection: Array<any>, pageSize: number) {
      return new Pager(collection, pageSize);
    },

    searchDataSource(operation: string, parameters?: Dictionary<any>) {
      return createSearchDataSource<object>(
        app.connections.default as Openapi,
        operation,
        parameters,
      );
    },

    slickDataSource(name: string, options?: SlickDataSourceOptions) {
      return createSlickDataSource(
        app,
        options ? _.extend(options, { name }) : { name },
      );
    },

    randomElementId() {
      return uniqueID();
    },

    newResourceID(prefix?: string) {
      return prefix ? `${prefix}${uniqueID()}` : uniqueID();
    },

    Resource,

    urlFor: app.connections.urlFor.bind(app.connections),

    poller(options: PollerOptions) {
      return new Poller(options);
    },

    resourceProxy(options: ResourceProxyOptions) {
      return new ResourceProxy(app, options);
    },

    imageData,

    numeral,

    humanizeDuration(duration: number) {
      return humanizeDuration(duration, {
        language: app.i18n.locale,
        maxDecimalPoints: 2,
      });
    },

    showFileInNewTab(url: string, fileName: string) {
      return Promise.resolve(downloads.getFileBLOB(url, fileName)).then(
        (payload) => payload && downloads.showFileInNewTab(payload.file, payload.fileName),
      );
    },

    withErrorHandler(value: any) {
      const handleError = (error: any) => {
        console.error(error);
        const imodal = (app as any).$imodal || (app as any).vueApp?.config?.globalProperties?.$imodal || (app as any).imodal;

        if (imodal) {
          imodal.error({ error });
        }
      };

      if (value && _.isFunction(value.catch)) {
        return value.catch((error: any) => handleError(error));
      }

      if (_.isFunction(value)) {
        return new Promise((resolve, reject) => {
          try {
            resolve(value());
          } catch (error) {
            reject(error);
          }
        }).catch((error: any) => handleError(error));
      }

      return value;
    },
  };
}
